package fr.labodoc.healthProfessional.webApp.pages.dashboard.components

import fr.labodoc.healthProfessional.webApp.App
import fr.labodoc.healthProfessional.webApp.components.labodocCheckbox
import fr.labodoc.healthProfessional.webApp.components.labodocSpinner
import fr.labodoc.healthProfessional.webApp.data.repository.MessagesRepository
import fr.labodoc.healthProfessional.webApp.pages.components.MessageModal
import fr.labodoc.healthProfessional.webApp.data.model.FlashInfoMessageSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.InvitationMessageSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.LabodocMessageSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.LaboratoryMessageSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.LearnedSocietyMessageSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.MedicineMessageSummaryModel
import fr.labodoc.healthProfessional.webApp.data.model.NewsSummaryModel
import fr.labodoc.utils.dateFormat
import io.kvision.core.onChange
import io.kvision.core.onClick
import io.kvision.form.check.CheckBox
import io.kvision.html.*
import io.kvision.panel.SimplePanel
import io.kvision.state.*
import io.kvision.toast.Toast
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import org.koin.core.component.KoinComponent
import org.koin.core.component.inject
import org.w3c.dom.Node

class Messages : SimplePanel(className = "messages") {
  private sealed interface ViewModel {
    sealed class UiState {
      abstract val filter: ObservableState<Filter>

      data class Loading(
        override val filter: ObservableState<Filter>
      ) : UiState()

      data class Error(
        override val filter: ObservableState<Filter>
      ) : UiState()

      data class Loaded(
        override val filter: ObservableState<Filter>,
        val messages: ObservableList<Message>,
        val unreadMessagesCount: ObservableState<Int>
      ) : UiState()
    }

    sealed class Message{
      abstract val id: String
      abstract val bannerUrl: String
      abstract val title: String
      abstract val author: String
      abstract val publishedAt: Instant
      abstract val favorite: ObservableValue<Boolean>
      abstract val seen: ObservableValue<Boolean>
    }

    data class News(
      override val id: String,
      override val bannerUrl: String,
      override val title: String,
      override val author: String,
      val newsUrl: String,
      override val publishedAt: Instant,
      override val favorite: ObservableValue<Boolean>,
      override val seen: ObservableValue<Boolean>
    ): Message()

    data class FlashInfo(
      override val id: String,
      override val bannerUrl: String,
      override val title: String,
      override val author: String,
      override val publishedAt: Instant,
      override val favorite: ObservableValue<Boolean>,
      override val seen: ObservableValue<Boolean>
    ): Message()

    data class Invitation(
      override val id: String,
      override val bannerUrl: String,
      override val title: String,
      override val author: String,
      override val publishedAt: Instant,
      override val favorite: ObservableValue<Boolean>,
      override val seen: ObservableValue<Boolean>
    ): Message()

    sealed class Filter {
      data object All : Filter()

      data object Favorites : Filter()

      data object Invitations : Filter()
    }

    val uiState: ObservableState<UiState>
    val messageModal: ObservableState<MessageModal?>

    fun refresh()
    fun updateFilter(filter: Filter)
    fun updateMessageFavoriteStatus(message: Message)
    fun getMessage(message: Message)
  }

  private class ViewModelImpl : ViewModel, KoinComponent {
    private val messageRepository: MessagesRepository by inject()

    private val filter: ObservableValue<ViewModel.Filter> =
      ObservableValue(ViewModel.Filter.All)

    private val unreadMessagesCount: ObservableValue<Int> =
      ObservableValue(0)

    private val messages: ObservableListWrapper<ViewModel.Message> =
      ObservableListWrapper()

    override val uiState: ObservableValue<ViewModel.UiState> by lazy {
      refresh()
      ObservableValue(ViewModel.UiState.Loading(filter))
    }

    override val messageModal: ObservableValue<MessageModal?> =
      ObservableValue(null)

    override fun refresh() {
      App.Companion.scope.launch {
        messageRepository
          .getMessages(
            onlyFavorite = filter.value is ViewModel.Filter.Favorites,
            flashInfo = filter.value !is ViewModel.Filter.Invitations,
            invitation = true
          )
          .fold(
            {
              ViewModel.UiState.Error(filter)
            },
            { response ->
              messages.clear()
              messages.addAll(
                response
                  .map { message ->
                    when (message) {
                      is NewsSummaryModel -> {
                        ViewModel.News(
                          id = message.id,
                          bannerUrl = message.bannerUrl,
                          title = message.title,
                          author = message.author,
                          newsUrl = message.newsUrl,
                          publishedAt = message.publishedAt,
                          favorite = ObservableValue(message.favorite),
                          seen = ObservableValue(message.seen)
                        )
                      }

                      is FlashInfoMessageSummaryModel -> {
                        ViewModel.FlashInfo(
                          id = message.id,
                          bannerUrl = message.bannerUrl,
                          title = message.title,
                          author = when (message) {
                            is LabodocMessageSummaryModel -> "LaboDoc"
                            is LaboratoryMessageSummaryModel -> message.laboratory.name
                            is MedicineMessageSummaryModel -> message.laboratory.name
                            is LearnedSocietyMessageSummaryModel -> message.learnedSociety.acronym ?: message.learnedSociety.name
                          },
                          publishedAt = message.publishedAt,
                          favorite = ObservableValue(message.favorite),
                          seen = ObservableValue(message.seen)
                        )
                      }

                      is InvitationMessageSummaryModel -> {
                        ViewModel.Invitation(
                          id = message.id,
                          bannerUrl = message.bannerUrl,
                          title = message.title,
                          author = when (message) {
                            is LabodocMessageSummaryModel -> "LaboDoc"
                            is LaboratoryMessageSummaryModel -> message.laboratory.name
                            is MedicineMessageSummaryModel -> message.laboratory.name
                            is LearnedSocietyMessageSummaryModel -> message.learnedSociety.acronym ?: message.learnedSociety.name
                          },
                          publishedAt = message.publishedAt,
                          favorite = ObservableValue(message.favorite),
                          seen = ObservableValue(message.seen)
                        )
                      }
                    }
                  }
              )

              unreadMessagesCount.value = messages.count { !it.seen.value }

              ViewModel.UiState.Loaded(
                filter = filter,
                messages = messages,
                unreadMessagesCount = unreadMessagesCount
              )
            }
          )
          .also { uiState.setState(it) }
      }
    }

    override fun updateFilter(filter: ViewModel.Filter) {
      App.Companion.scope.launch {
        this@ViewModelImpl.filter.value = filter

        refresh()
      }
    }

    override fun updateMessageFavoriteStatus(message: ViewModel.Message) {
      App.Companion.scope.launch {
        val response = if (!message.favorite.value)
          messageRepository.favorite(message.id)
        else
          messageRepository.unfavorite(message.id)

        response
          .onLeft {
            Toast.danger("Une erreur est survenue lors de la mise à jour du favori")
            message.favorite.value = message.favorite.value // Force the bound checkbox to re-match the message favorite state
          }
          .onRight {
            message.favorite.value = !message.favorite.value
          }
      }
    }

    override fun getMessage(message: ViewModel.Message) {
      App.Companion.scope.launch {
        messageRepository.getMessage(message.id)
          .onRight {
            message.seen.value = true
            unreadMessagesCount.value = messages.count { !it.seen.value }

            if (message is ViewModel.News)
              window.open(url = message.newsUrl, target = "_blank")
            else {
              messageModal
                .setState(
                  MessageModal(
                    message = it,
                    onFavoriteClicked = { updateMessageFavoriteStatus(message) }
                  )
                )
            }
          }
      }
    }
  }

  private class MessageCard(
    message: ViewModel.Message,
    onCardClicked: () -> Unit,
    onFavoriteClicked: () -> Unit
  ) : SimplePanel(className = "message") {
    private val messageBanner = if (!message.bannerUrl.contains("placeholder")) {
      Image(
        src = message.bannerUrl
      )
    } else
      null

    private val messageType = P(className = "type") {
      when (message) {
        is ViewModel.FlashInfo, is ViewModel.News -> {
          icon("fa-solid fa-bell")
          content = "Flash Info"
        }

        is ViewModel.Invitation -> {
          icon("fa-solid fa-envelope")
          content = "Invitation"
        }
      }
    }

    private val messageDate = P(message.publishedAt.dateFormat, className = "created-date")

    private val messageFavoriteButton = CheckBox {
      addCssClass("favorite")

      onChange {
        onFavoriteClicked()
      }

      onClick { event ->
        event.stopPropagation()
      }
    }.bind(message.favorite) {
      value = it
    }

    private val messageTitle = P(message.title, className = "title")

    private val messageAuthor = P(message.author, className = "author")

    init {
      header {
        div {
          add(messageType)

          add(messageDate)

          add(messageFavoriteButton)
        }

        messageBanner?.let { add(it) }
      }

      add(messageTitle)

      add(messageAuthor)

      onClick { event ->
        val target = event.target

        if (target !is Node || messageFavoriteButton.getElement()?.contains(target) != true)
          onCardClicked()
      }

      bind(message.seen, removeChildren = false) {
        if (!it)
          addCssClass("new")
        else
          removeCssClass("new")
      }
    }
  }

  private val viewModel: ViewModel = ViewModelImpl()

  init {
    bind(viewModel.uiState) { uiState ->
      header {
        if (uiState is ViewModel.UiState.Loaded) {
          div(className = "welcome-message").bind(uiState.unreadMessagesCount) { unreadMessagesCount ->
            icon("fa-solid fa-envelope") {
              if (unreadMessagesCount > 0)
                span(unreadMessagesCount.toString(), className = "message-number")
            }
            p {
              val messageCountSentence = if (unreadMessagesCount > 0)
                "Vous avez $unreadMessagesCount nouvelles actualités"
              else
                "Vous avez 0 nouvelle actualité"

              rich = true
              content =
                """<span class="hello">Bonjour ${App.Companion.user.getState()?.firstName ?: ""}</span>,<br>${messageCountSentence}"""
            }
          }
        } else {
          div(className = "welcome-message") {
            icon("fa-solid fa-envelope")
            p {
              rich = true
              content =
                """<span class="hello">Bonjour ${App.Companion.user.getState()?.firstName ?: ""}</span>,<br>Vous avez 0 nouvelle actualité"""
            }
          }
        }

        div(className = "filters").bind(uiState.filter) { filter ->
          labodocCheckbox {
            label = "Toutes"
            value = filter is ViewModel.Filter.All

            onChange { viewModel.updateFilter(ViewModel.Filter.All) }
          }
          labodocCheckbox {
            label = "Favoris"
            value = filter is ViewModel.Filter.Favorites

            onChange { viewModel.updateFilter(ViewModel.Filter.Favorites) }
          }
          labodocCheckbox {
            label = "Invitations"
            value = filter is ViewModel.Filter.Invitations

            onChange { viewModel.updateFilter(ViewModel.Filter.Invitations) }
          }
        }
      }

      div(className = "container") {
        when (uiState) {
          is ViewModel.UiState.Loading -> labodocSpinner()

          is ViewModel.UiState.Error -> {
            div(className = "no-messages") {
              p("Impossible de charger les actualités", className = "text")
            }
          }

          is ViewModel.UiState.Loaded -> {
            if (uiState.messages.isEmpty()) {
              div(className = "no-messages").bind(uiState.filter) { filter ->
                when (filter) {
                  ViewModel.Filter.Favorites -> {
                    p("Aucune actualité en favoris", className = "text")
                  }

                  else -> {
                    p("Actuellement, vous n'avez pas d'actualités pour votre séléction", className = "text")
                    p("Vous serez alerté en cas de nouvelles actualités", className = "tip")
                  }
                }
              }
            } else {
              div(className = "list").bindEach(uiState.messages) { message ->
                add(
                  MessageCard(
                    message = message,
                    onCardClicked = { viewModel.getMessage(message) },
                    onFavoriteClicked = { viewModel.updateMessageFavoriteStatus(message) }
                  )
                )
              }
            }
          }
        }
      }
    }

    viewModel.messageModal.subscribe {
      it?.show()
    }
  }
}
